import { useState } from "react";
import type { FormEvent } from "react";
import { Button, Card, EmptyState, Field, Input, Select, Spinner } from "@empite/ui-components";
import type { Comment } from "../marketplace-client";
import {
  useAddComment,
  useComments,
  useDeleteComment,
  useEditComment,
  useModerateComment,
  useReportReasons,
  useViewer,
} from "../marketplace-hooks";
import { ErrorAlert, SignInPrompt, when } from "./common";
import { ReportForm } from "./report";

/**
 * The conversation under a creation.
 *
 * The list the server returns is VISIBLE comments only — a hidden or removed
 * one is not in the payload at all — so this screen has no "hidden" branch
 * and cannot accidentally render one. Replies are bounded to one level by the
 * server, which is why the reply control only appears on a top-level comment:
 * a reply to a reply is a 422, and offering the button would be offering a
 * failure.
 */
export function Comments({
  creationID,
  isCreationAuthor,
}: {
  creationID: string;
  isCreationAuthor: boolean;
}) {
  const viewer = useViewer();
  const comments = useComments(creationID);
  const [replyTo, setReplyTo] = useState<string | null>(null);

  const rows = comments.data ?? [];
  const roots = rows.filter((c) => !c.parent_comment_id);
  const repliesOf = (id: string) => rows.filter((c) => c.parent_comment_id === id);

  return (
    <Card title={`Comments (${rows.length})`}>
      <ErrorAlert error={comments.error} title="We could not load the comments" />
      {viewer.signedIn ? (
        <CommentForm creationID={creationID} onPosted={() => setReplyTo(null)} />
      ) : viewer.ready ? (
        <SignInPrompt what="the comment box" />
      ) : null}

      {comments.isPending ? (
        <Spinner label="Loading comments" />
      ) : roots.length === 0 ? (
        <EmptyState title="No comments yet" />
      ) : (
        <ul className="esm-comment-list">
          {roots.map((comment) => (
            <li key={comment.id}>
              <CommentRow
                comment={comment}
                isCreationAuthor={isCreationAuthor}
                canReply
                replying={replyTo === comment.id}
                onReply={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
              />
              <ul className="esm-comment-list esm-comment-list--replies">
                {repliesOf(comment.id).map((reply) => (
                  <li key={reply.id}>
                    <CommentRow
                      comment={reply}
                      isCreationAuthor={isCreationAuthor}
                      canReply={false}
                      replying={false}
                      onReply={() => undefined}
                    />
                  </li>
                ))}
              </ul>
              {replyTo === comment.id && viewer.signedIn ? (
                <CommentForm
                  creationID={creationID}
                  parentID={comment.id}
                  onPosted={() => setReplyTo(null)}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function CommentForm({
  creationID,
  parentID,
  onPosted,
}: {
  creationID: string;
  parentID?: string;
  onPosted: () => void;
}) {
  const add = useAddComment();
  const [body, setBody] = useState("");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (body.trim() === "") {
      return;
    }
    add.mutate(
      { creationID, body: { body: body.trim(), parent_comment_id: parentID } },
      {
        onSuccess: () => {
          setBody("");
          onPosted();
        },
      },
    );
  };

  return (
    <form className="esm-comment-form" onSubmit={submit} noValidate>
      <ErrorAlert error={add.error} title="Your comment was not posted" />
      <Field label={parentID ? "Your reply" : "Add a comment"} htmlFor={`comment-${parentID ?? "root"}`}>
        <Input
          id={`comment-${parentID ?? "root"}`}
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
      </Field>
      <Button type="submit" busy={add.isPending}>
        {parentID ? "Reply" : "Post"}
      </Button>
    </form>
  );
}

function CommentRow({
  comment,
  isCreationAuthor,
  canReply,
  replying,
  onReply,
}: {
  comment: Comment;
  isCreationAuthor: boolean;
  canReply: boolean;
  replying: boolean;
  onReply: () => void;
}) {
  const viewer = useViewer();
  const edit = useEditComment();
  const remove = useDeleteComment();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [reporting, setReporting] = useState(false);

  const mine = viewer.userID !== null && viewer.userID === comment.author_user_id;

  return (
    <article className="esm-comment">
      <header className="esm-comment__meta">
        <strong>{comment.author_display_name || "A suite user"}</strong>
        <span>{when(comment.created_at)}</span>
        {comment.edited_at ? <span>edited</span> : null}
      </header>

      {editing ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            edit.mutate(
              { commentID: comment.id, body: draft.trim() },
              { onSuccess: () => setEditing(false) },
            );
          }}
          noValidate
        >
          {/* The window is the server's and it is short. A rejected edit says
              so rather than silently discarding what was typed. */}
          <ErrorAlert error={edit.error} title="That edit was refused" />
          <Input value={draft} onChange={(event) => setDraft(event.target.value)} />
          <div className="esm-actions">
            <Button type="submit" busy={edit.isPending}>
              Save
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <p className="esm-comment__body">{comment.body}</p>
      )}

      <ErrorAlert error={remove.error} title="That comment was not deleted" />
      <div className="esm-actions">
        {canReply && viewer.signedIn ? (
          <Button variant="ghost" onClick={onReply}>
            {replying ? "Cancel reply" : "Reply"}
          </Button>
        ) : null}
        {mine ? (
          <>
            <Button variant="ghost" onClick={() => setEditing((was) => !was)}>
              Edit
            </Button>
            <Button
              variant="ghost"
              busy={remove.isPending}
              onClick={() => remove.mutate(comment.id)}
            >
              Delete
            </Button>
          </>
        ) : null}
        {isCreationAuthor && !mine ? <AuthorModeration commentID={comment.id} /> : null}
        {viewer.signedIn && !mine ? (
          <Button variant="ghost" onClick={() => setReporting((was) => !was)}>
            Report
          </Button>
        ) : null}
      </div>

      {reporting ? (
        <ReportForm
          subject={{ commentID: comment.id }}
          what="comment"
          onDone={() => setReporting(false)}
        />
      ) : null}
    </article>
  );
}

/**
 * The creation author moderating a comment under their own work.
 *
 * The same verbs an operator uses and the same append-only trail, but a
 * different authority: the row records actor_user_id and never
 * admin_user_id, so an author's decision cannot be read back as an
 * operator's. The reason is mandatory for the reason it is mandatory in the
 * back office — a hide nobody can explain cannot be reviewed.
 *
 * There is no Restore control, and that is a gap rather than a policy: the
 * comment list returns VISIBLE comments only, so a hidden comment has nowhere
 * in this portal to be restored from.
 */
function AuthorModeration({ commentID }: { commentID: string }) {
  const reasons = useReportReasons();
  const moderate = useModerateComment();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");

  if (!open) {
    return (
      <Button variant="ghost" onClick={() => setOpen(true)}>
        Moderate
      </Button>
    );
  }

  const act = (action: "hide" | "remove") => {
    if (code === "") {
      return;
    }
    moderate.mutate(
      { commentID, action, body: { reason_code: code, note: "" } },
      { onSuccess: () => setOpen(false) },
    );
  };

  return (
    <div className="esm-moderate">
      <ErrorAlert error={moderate.error} title="That moderation action was refused" />
      <Field label="Reason" htmlFor={`moderate-${commentID}`}>
        <Select
          id={`moderate-${commentID}`}
          value={code}
          onChange={(event) => setCode(event.target.value)}
        >
          <option value="">Pick a reason…</option>
          {(reasons.data ?? []).map((reason) => (
            <option key={reason} value={reason}>
              {reason}
            </option>
          ))}
        </Select>
      </Field>
      <div className="esm-actions">
        <Button variant="secondary" busy={moderate.isPending} onClick={() => act("hide")}>
          Hide
        </Button>
        <Button variant="danger" busy={moderate.isPending} onClick={() => act("remove")}>
          Remove
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
